import { RootState } from '@/store/store';
import type {
  Address,
  DecoratedCompany,
  DecoratedCompanyFile,
  DecoratedCompanyInvestor,
  DecoratedCompanyMember,
  DecoratedCompanyPost,
  DecoratedInvestor,
  DecoratedInvestorCompany,
  DecoratedMemberCompany,
  DecoratedPostFile,
  DecoratedPostWithAttachments,
  DecoratedProperty,
  DecoratedUser,
  Notification,
  UndecoratedCompany,
  UndecoratedFile,
  UndecoratedFinancialReport,
  UndecoratedInvestment,
  UndecoratedInvestor,
  UndecoratedUser,
} from '@flemingmetrics/api-client';
import { PREFERENCES } from '@flemingmetrics/api-client';
import { buildCompositeKey, type Identifier } from '@/store/utils';
import type {
  Profile,
  ProfilePreferences,
  ProfileProperties,
} from '@/types/profiles';
import { buildProfilePreferenceKey } from '@/store/slices/profilePreferencesSlice';
import { DateFormat } from '@/constants/dateFormatPreference';
import { WeekStartsOn } from '@/types/dates';
import { isAdmin } from '@/utils/users';
import { buildCompanyMemberKey } from '@/store/slices/companyMembersSlice';
import { getLatestReport } from '@/utils/reports';
import { findById } from '@/utils/arrays';

function buildKey(identifier: Identifier | Identifier[]) {
  return Array.isArray(identifier)
    ? buildCompositeKey(...identifier)
    : `${identifier}`;
}

export function filterBlanks<T>(arr: (T | null | undefined)[]): T[] {
  return arr.filter((item): item is T => item !== null && item !== undefined);
}

function buildFromIdArray<T, IdType>(
  state: RootState,
  sliceName: keyof Omit<
    RootState,
    'currentUser' | 'currentInvestor' | 'unreadNotificationsCount'
  >,
  buildMethod: (state: RootState, id: IdType) => any,
  idArray: IdType[]
): T[] {
  if (!idArray) return [];
  return filterBlanks(
    idArray
      .filter((id) => Object.keys(state[sliceName].byId).includes(`${id}`))
      .map((id) => buildMethod(state, id))
  );
}

export function buildCurrentUser(
  state: RootState
): UndecoratedUser | undefined {
  if (!state.currentUser) return undefined;
  return { ...state.currentUser };
}

export function buildCurrentProfile(state: RootState): Profile | undefined {
  if (!state.currentUser) return undefined;
  const profileId = state.profiles.byUserId[state.currentUser.id];
  if (!profileId) return undefined;
  return buildProfile(state, profileId);
}

export function buildProfile(
  state: RootState,
  profileId: Identifier
): Profile | undefined {
  const key = buildKey(profileId);
  if (!state.profiles.byId[key]) return undefined;
  return {
    ...state.profiles.byId[key],
    preferences: buildProfilePreferences(state, profileId),
    properties: buildProfileProperties(state, profileId),
  };
}

export function buildCurrentProfilePreference(
  state: RootState,
  preferenceId: number
) {
  if (!state.currentUser) return undefined;
  const profileId = state.profiles.byUserId[state.currentUser.id];
  if (!profileId) return undefined;
  const key = buildProfilePreferenceKey(profileId, preferenceId);
  const profilePreference = state.profilePreferences.byId[key];
  if (!profilePreference) return undefined;
  return profilePreference.value;
}

export function buildCurrentProfilePreferences(
  state: RootState
): ProfilePreferences {
  if (!state.currentUser) return {} as ProfilePreferences;
  const profileId = state.profiles.byUserId[state.currentUser.id];
  return buildProfilePreferences(state, profileId);
}

export function buildCurrentProfileProperties(
  state: RootState
): ProfileProperties {
  if (!state.currentUser) return {} as ProfileProperties;
  const profileId = state.profiles.byUserId[state.currentUser.id];
  return buildProfileProperties(state, profileId);
}

export function buildDateFormatPreference(state: RootState) {
  return buildCurrentProfilePreference(state, PREFERENCES.DATE_FORMAT.id) as
    | DateFormat
    | undefined;
}

export function buildProfilePreferences(
  state: RootState,
  profileId: Identifier
): ProfilePreferences {
  const key = buildKey(profileId);
  if (!state.profilePreferences.byProfileId[key])
    return {} as ProfilePreferences;
  return state.profilePreferences.byProfileId[key].reduce((a, b) => {
    const profilePreference = state.profilePreferences.byId[b];
    const preference = findById(
      Object.values(PREFERENCES),
      profilePreference.preferenceId
    )!;
    return { ...a, [preference.name]: { ...profilePreference, preference } };
  }, {} as ProfilePreferences);
}

export function buildWeekStartsOnPreference(state: RootState) {
  const preference = buildCurrentProfilePreference(
    state,
    PREFERENCES.WEEK_STARTS_ON.id
  );
  if (!preference) return 0;
  return parseInt(preference as string) as WeekStartsOn;
}

export function buildProfileProperties(
  state: RootState,
  profileId: Identifier
): ProfileProperties {
  const key = buildKey(profileId);
  if (!state.profileProperties.byProfileId[key]) return {} as ProfileProperties;
  return state.profileProperties.byProfileId[key].reduce((a, b) => {
    const profileProperty = state.profileProperties.byId[b];
    const property = buildProperty(state, profileProperty.propertyId)!;
    return { ...a, [property.name]: { ...profileProperty, property } };
  }, {} as ProfileProperties);
}

export function buildProperty(
  state: RootState,
  propertyId: Identifier
): DecoratedProperty | undefined {
  const key = buildKey(propertyId);
  if (!state.properties.byId[key]) return undefined;
  return { ...state.properties.byId[key] };
}

export function buildAllCompanies(state: RootState): UndecoratedCompany[] {
  const idArray = Object.keys(state.companies.byId);
  return buildCompaniesFromIdArray(state, idArray);
}

export function buildCompaniesFromIdArray(
  state: RootState,
  idArray: Identifier[]
): UndecoratedCompany[] {
  return buildFromIdArray<UndecoratedCompany, Identifier>(
    state,
    'companies',
    buildCompany,
    idArray
  );
}

export function buildCompany(
  state: RootState,
  companyId: Identifier
): DecoratedCompany | undefined {
  const key = buildKey(companyId);
  if (!state.companies.byId[key]) return undefined;
  return {
    ...state.companies.byId[key],
    address: buildAddress(state, state.companies.byId[key].addressId),
  };
}

export function buildAddress(
  state: RootState,
  addressId: Identifier | null
): Address | undefined {
  if (!addressId) return undefined;
  const key = buildKey(addressId);
  if (!state.addresses.byId[key]) return undefined;
  return { ...state.addresses.byId[key] };
}

export function buildCompanyFiles(
  state: RootState,
  companyId: Identifier
): DecoratedCompanyFile[] {
  if (!companyId) return [];
  const key = buildKey(companyId);
  const companyFileKeys = state.companyFiles.byCompanyId[key];
  if (!companyFileKeys) return [];
  return buildFromIdArray<DecoratedCompanyFile, string>(
    state,
    'companyFiles',
    buildCompanyFile,
    companyFileKeys
  );
}

export function buildCompanyFile(
  state: RootState,
  companyFileKey: Identifier
): DecoratedCompanyFile | undefined {
  const companyFile = state.companyFiles.byId[companyFileKey];
  if (!companyFile) return undefined;
  const file = buildFile(state, companyFile.fileId);
  if (!file) return undefined;
  return { ...companyFile, file };
}

export function buildCompanyFilesFromIdArray(
  state: RootState,
  idArray: Identifier[]
): DecoratedCompanyFile[] {
  return buildFromIdArray<DecoratedCompanyFile, Identifier>(
    state,
    'companyFiles',
    buildCompanyFile,
    idArray
  );
}

export function buildFilesFromIdArray(
  state: RootState,
  idArray: Identifier[]
): UndecoratedFile[] {
  return buildFromIdArray<UndecoratedFile, Identifier>(
    state,
    'files',
    buildFile,
    idArray
  );
}

export function buildFile(
  state: RootState,
  fileId: Identifier
): UndecoratedFile | undefined {
  const key = buildKey(fileId);
  if (!state.files.byId[key]) return undefined;
  return { ...state.files.byId[key] };
}

export function buildAllUsers(state: RootState): DecoratedUser[] {
  const idArray = Object.keys(state.users.byId);
  return buildUsersFromIdArray(state, idArray);
}

export function buildUsersFromIdArray(
  state: RootState,
  idArray: Identifier[]
): DecoratedUser[] {
  return buildFromIdArray<DecoratedUser, Identifier>(
    state,
    'users',
    buildUser,
    idArray
  );
}

export function buildUser(
  state: RootState,
  userId?: Identifier
): DecoratedUser | undefined {
  if (!userId) return undefined;
  const key = buildKey(userId);
  const user = state.users.byId[key];
  if (!user) return undefined;
  const profileId = state.profiles.byUserId[user.id];
  if (!profileId) return undefined;
  const profile = buildProfile(state, profileId);
  if (!profile) return undefined;
  return { ...user, profile };
}

export function buildAllInvestors(state: RootState): DecoratedInvestor[] {
  const idArray = Object.keys(state.investors.byId);
  return buildInvestorsFromIdArray(state, idArray);
}

export function buildInvestorsFromIdArray(
  state: RootState,
  idArray: Identifier[]
): DecoratedInvestor[] {
  return buildFromIdArray<DecoratedInvestor, Identifier>(
    state,
    'investors',
    buildInvestor,
    idArray
  );
}

export function buildInvestor(
  state: RootState,
  investorId?: Identifier
): DecoratedInvestor | undefined {
  if (!investorId) return undefined;
  const key = buildKey(investorId);
  const investor = state.investors.byId[key];
  if (!investor) return undefined;
  return {
    ...investor,
    profile: buildProfile(state, investor.profileId)!,
  };
}

export function buildInvestorCompanies(
  state: RootState,
  investorId?: Identifier
): DecoratedInvestorCompany[] {
  if (!investorId) return [];
  const key = buildKey(investorId);
  const companyInvestorIds = state.companyInvestors.byInvestorId[key];
  if (!companyInvestorIds) return [];
  return buildFromIdArray<DecoratedInvestorCompany, Identifier>(
    state,
    'companyInvestors',
    buildInvestorCompany,
    companyInvestorIds
  );
}

export function buildInvestorCompany(
  state: RootState,
  companyInvestorKey: Identifier
): DecoratedInvestorCompany | undefined {
  const companyInvestor = state.companyInvestors.byId[companyInvestorKey];
  if (!companyInvestor) return undefined;
  const { companyId, investorId } = companyInvestor;
  const company = buildCompany(state, companyId);
  if (!company) return undefined;
  const investments = buildInvestorCompanyInvestments(
    state,
    companyId,
    investorId
  );
  return { ...companyInvestor, company, investments };
}

export function buildInvestorCompanyInvestments(
  state: RootState,
  companyId: Identifier,
  investorId: Identifier
) {
  // TODO: This can probably be optimized/simplified
  const idArray = state.investments.byInvestorId[investorId];
  if (!idArray) return [];
  const investments = buildFromIdArray<UndecoratedInvestment, string>(
    state,
    'investments',
    buildInvestment,
    idArray
  );
  return investments.filter((investment) => investment.companyId === companyId);
}

export function buildInvestment(
  state: RootState,
  investmentId: Identifier
): UndecoratedInvestment | undefined {
  const key = buildKey(investmentId);
  if (!state.investments.byId[key]) return undefined;
  return { ...state.investments.byId[key] };
}

export function buildCompanyPosts(
  state: RootState,
  companyId: Identifier
): DecoratedCompanyPost[] {
  if (!companyId) return [];
  const key = buildKey(companyId);
  const companyPostKeys = state.companyPosts.byCompanyId[key];
  if (!companyPostKeys) return [];
  return buildFromIdArray<DecoratedCompanyPost, string>(
    state,
    'companyPosts',
    buildCompanyPost,
    companyPostKeys
  );
}

export function buildCompanyPost(
  state: RootState,
  companyPostKey: Identifier
): DecoratedCompanyPost | undefined {
  const companyPost = state.companyPosts.byId[companyPostKey];
  if (!companyPost) return undefined;
  const post = buildPost(state, companyPost.postId);
  if (!post) return undefined;
  return { ...companyPost, post };
}

export function buildCompanyPostsFromIdArray(
  state: RootState,
  idArray: Identifier[]
): DecoratedCompanyPost[] {
  return buildFromIdArray<DecoratedCompanyPost, Identifier>(
    state,
    'companyPosts',
    buildCompanyPost,
    idArray
  );
}

export function buildAllCompanyPosts(state: RootState): DecoratedCompanyPost[] {
  const idArray = Object.keys(state.companyPosts.byId);
  return buildCompanyPostsFromIdArray(state, idArray);
}

export function buildPost(
  state: RootState,
  postId?: Identifier
): DecoratedPostWithAttachments | undefined {
  if (!postId) return undefined;
  const key = buildKey(postId);
  const post = state.posts.byId[key];
  const postFiles = buildPostFiles(state, postId);
  return post ? { ...post, postFiles } : undefined;
}

export function buildPostFiles(
  state: RootState,
  postId: Identifier
): DecoratedPostFile[] {
  if (!postId) return [];
  const key = buildKey(postId);
  const postFileKeys = state.postFiles.byPostId[key];
  if (!postFileKeys) return [];
  return buildFromIdArray<DecoratedPostFile, string>(
    state,
    'postFiles',
    buildPostFile,
    postFileKeys
  );
}

export function buildPostFile(
  state: RootState,
  postFileKey: Identifier
): DecoratedPostFile | undefined {
  const postFile = state.postFiles.byId[postFileKey];
  if (!postFile) return undefined;
  const file = buildFile(state, postFile.fileId);
  if (!file) return undefined;
  return { ...postFile, file };
}

export function buildCompanyMembers(
  state: RootState,
  companyId: Identifier
): DecoratedCompanyMember[] {
  if (!companyId) return [];
  const key = buildKey(companyId);
  const companyMemberIds = state.companyMembers.byCompanyId[key];
  if (!companyMemberIds) return [];
  return buildFromIdArray<DecoratedCompanyMember, string>(
    state,
    'companyMembers',
    buildCompanyMember,
    companyMemberIds
  );
}

export function buildMemberCompanies(
  state: RootState,
  profileId: Identifier
): DecoratedMemberCompany[] {
  if (!profileId) return [];
  const key = buildKey(profileId);
  const companyMemberIds = state.companyMembers.byProfileId[key];
  if (!companyMemberIds) return [];
  return buildFromIdArray<DecoratedMemberCompany, Identifier>(
    state,
    'companyMembers',
    buildMemberCompany,
    companyMemberIds
  );
}

export function buildMemberCompany(
  state: RootState,
  companyMemberKey: Identifier
): DecoratedMemberCompany | undefined {
  const member = state.companyMembers.byId[companyMemberKey];
  if (!member) return undefined;
  const company = buildCompany(state, member.companyId);
  if (!company) return undefined;
  return { ...member, company };
}

export function buildCompanyMember(
  state: RootState,
  companyMemberKey: Identifier
): DecoratedCompanyMember | undefined {
  const member = state.companyMembers.byId[companyMemberKey];
  if (!member) return undefined;
  const profile = buildProfile(state, member.profileId);
  if (!profile) return undefined;
  return { ...member, profile };
}

export function buildCurrentInvestor(
  state: RootState
): UndecoratedInvestor | undefined {
  if (!state.currentInvestor) return undefined;
  return { ...state.currentInvestor };
}

export function buildCompanyInvestors(
  state: RootState,
  companyId: Identifier
): DecoratedCompanyInvestor[] {
  if (!companyId) return [];
  const key = buildKey(companyId);
  const companyInvestorIds = state.companyInvestors.byCompanyId[key];
  if (!companyInvestorIds) return [];
  return buildFromIdArray<DecoratedCompanyInvestor, string>(
    state,
    'companyInvestors',
    buildCompanyInvestor,
    companyInvestorIds
  );
}

export function buildCompanyInvestor(
  state: RootState,
  companyInvestorKey: Identifier
): DecoratedCompanyInvestor | undefined {
  const companyInvestor = state.companyInvestors.byId[companyInvestorKey];
  if (!companyInvestor) return undefined;
  const investor = buildInvestor(state, companyInvestor.investorId);
  if (!investor) return undefined;
  const investments = buildInvestorCompanyInvestments(
    state,
    companyInvestor.companyId,
    companyInvestor.investorId
  );
  return { ...companyInvestor, investor, investments };
}

export function buildCompanyFinancialReports(
  state: RootState,
  companyId: Identifier
): UndecoratedFinancialReport[] {
  if (!companyId) return [];
  const key = buildKey(companyId);
  const reportIds = state.financialReports.byCompanyId[key];
  if (!reportIds) return [];
  return buildFromIdArray<UndecoratedFinancialReport, string>(
    state,
    'financialReports',
    buildFinancialReport,
    reportIds
  );
}

export function buildFinancialReport(
  state: RootState,
  reportId: Identifier
): UndecoratedFinancialReport | undefined {
  const key = buildKey(reportId);
  const report = state.financialReports.byId[key];
  if (!report) return undefined;
  return { ...report };
}

export function buildLatestFinancialReport(
  state: RootState,
  companyId?: Identifier
) {
  if (!companyId) return undefined;
  return getLatestReport(buildCompanyFinancialReports(state, companyId));
}

export function buildLatestFinancialReportsFromIdArray(
  state: RootState,
  companyIds?: Identifier[]
) {
  if (!companyIds || companyIds.length === 0) return [];
  return filterBlanks(
    companyIds.map((companyId) =>
      getLatestReport(buildCompanyFinancialReports(state, companyId))
    )
  );
}

export function buildHasCompanyAccess(
  state: RootState,
  companyId?: Identifier
) {
  if (isAdmin(state.currentUser)) return true;
  if (!state.currentUser || !companyId) return false;
  const profile = buildCurrentProfile(state);
  if (!profile) return false;
  const key = buildCompanyMemberKey(companyId, profile.id);
  return !!state.companyMembers.byId[key];
}

export function buildNotification(
  state: RootState,
  notificationId: Identifier
) {
  const key = buildKey(notificationId);
  return state.notifications.byId[key] || undefined;
}

export function buildAllNotifications(state: RootState) {
  const idArray = Object.keys(state.notifications.byId);
  return buildFromIdArray<Notification, Identifier>(
    state,
    'notifications',
    buildNotification,
    idArray
  );
}

export function buildNotificationsFromIdArray(
  state: RootState,
  idArray: Identifier[]
) {
  return buildFromIdArray<Notification, Identifier>(
    state,
    'notifications',
    buildNotification,
    idArray
  );
}

export function buildUnreadNotificationsCount(state: RootState) {
  return state.unreadNotificationsCount;
}
